'use strict';

(function (global) {

  /**
   * Класс-обертка для панели действий.
   *
   * @class WebAccess.components.wrappers.toolBar.ReactToolBar
   * @extend BaseReactObject
   * @since 5.6.0.1300
   */
  class ReactToolBar extends BaseReactObject {

    /**
     * Конструктор.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#init
     * @param {object} model Модель панели действий.
     * @override
     * @see BaseReactObject#init
     */
    constructor(model) {
      super(model);
      
      /**
       * Имя класса.
       *
       * @private
       * @property WebAccess.components.wrappers.toolBar.ReactToolBar#className
       * @override
       * @see BaseReactObject#className
       */
      this.className = 'ReactToolBar';

      /**
       * Список событий изменения кнопок.
       *
       * @private
       * @property WebAccess.components.wrappers.toolBar.ReactToolBar#_buttonChangeEvents
       */
      this._buttonChangeEvents = null;

      /**
       * Список действий и соответствующих им кнопок на ленте.
       *
       * @private
       * @property WebAccess.components.wrappers.toolBar.ReactToolBar#_buttonsByActionName
       */
      this._buttonsByActionName = {};

      /**
       * Список режимов доступности и соответствующих им действий.
       *
       * @private
       * @property WebAccess.components.wrappers.toolBar.ReactToolBar#_buttonsByEnabledMode
       */
      this._buttonsByEnabledMode = {};

      /**
       * Список кнопок, состояние которых изменяется в зависимости от выделения вложений.
       *
       * @private
       * @property WebAccess.components.wrappers.toolBar.ReactToolBar#_buttonsEnabledOnAttachmentSelection
       */
      this._buttonsEnabledOnAttachmentSelection = [];

      /**
       * Список событий изменения групп.
       *
       * @private
       * @property WebAccess.components.wrappers.toolBar.ReactToolBar#_groupChangeEvents
       */
      this._groupChangeEvents = null;

      /**
       * Список кнопок панели действий.
       *
       * @property WebAccess.components.wrappers.toolBar.ReactToolBar#buttons
       * @deprecated since 5.6. Use toolbar methods instead.
       */
      this.buttons = {};

      /**
       * Список групп кнопок панели действий.
       *
       * @property WebAccess.components.wrappers.toolBar.ReactToolBar#groups
       * @deprecated since 5.6. Use toolbar methods instead.
       */
      this.groups = {};
      
      var me = this;

      me.buttons = {};
      me.groups = {};


      // Сформировать списки событий
      var basePageElementEvents = global.utils.getAssociativeArrayValues(WebAccess.components.wrappers.AbstractBasePageElement.EVENT);
      me._groupChangeEvents = basePageElementEvents.concat(
        global.utils.getAssociativeArrayValues(WebAccess.components.wrappers.toolBar.ReactToolBarGroup.EVENT)
      );
      me._buttonChangeEvents = basePageElementEvents.concat(
        global.utils.getAssociativeArrayValues(WebAccess.components.wrappers.toolBar.ReactToolBarButton.EVENT)
      );

      // Инициализируем список групп
      model.groups.forEach(function (groupModel) {
        var currentGroup = new WebAccess.components.wrappers.toolBar.ReactToolBarGroup(groupModel);
        me.groups[groupModel.name] = currentGroup;

        // Подписаться на события изменения группы
        me._bindOnGroupChangeEvents(currentGroup);

        // Добавить список кнопок в группе в buttons
        groupModel.buttons.forEach(function (buttonModel) {
          var currentButton = new WebAccess.components.wrappers.toolBar.ReactToolBarButton(buttonModel, groupModel.name);
          me.buttons[buttonModel.name] = currentButton;

          // Подписаться на события изменения кнопки
          me._bindOnButtonChangeEvents(currentButton);
        });
      });

      // Сформировать списки
      global.utils.getAssociativeArrayValues(me.buttons).forEach(function (item) {
        // Действия и соответствующие им кнопки
        var linkedAction = item.getLinkedAction();
        var buttons = null;
        if (linkedAction) {
          buttons = me._buttonsByActionName[linkedAction];
          if (buttons) {
            buttons.push(item);
          } else {
            me._buttonsByActionName[linkedAction] = [item];
          }
        }

        // Режимы доступности и соответствующие им кнопки
        var enabledMode = item.getEnabledMode();
        if (enabledMode) {
          buttons = me._buttonsByEnabledMode[enabledMode];
          if (buttons) {
            buttons.push(item);
          } else {
            me._buttonsByEnabledMode[enabledMode] = [item];
          }
        }

        // Сформировать список кнопок, состояния которых изменяются в зависимости от выделения элементов
        if (item.getIsEnabledOnAttachnentSelected()) {
          me._buttonsEnabledOnAttachmentSelection.push(item);
        }
      });

      // Расширить коллекции
      me._extendGroupCollection();
      me._extendButtonCollection();
    }

    /**
     * Признак скрытого тулбара.
     *
     * @method getIsHidden
     * @return {boolean} True - тулбар скрыт, иначе False.
     */
    getIsHidden() {
      return this._model.isHidden;
    }

    /**
     * Список событий, генерируемых реактивным тулбаром.
     *
     * @property EVENT
     * @return {{CHANGE_STATE: string}}
     * @static
     */
    static get EVENT() {
      return {
        // Событие, срабатывающее при изменении модели панели действий.
        CHANGE_STATE: 'changeState'
      };
    }

    /**
     * Проверка на наличие кнопок, которые можно свернуть.
     *
     * @method _hasExpandedButtons
     * @param {object} group Группа, в которой проверяется наличие таких кнопок.
     * @private
     * @return {boolean} True - кнопки, которые можно свернуть, присутствуют, иначе False.
     * @static
     *
     */
    static _hasExpandedButtons(group) {
      return group.buttons.some((button) => {
        return !group.isCollapsed && button.isCaptionVisible && !button.isCollapsed;
      });
    }

    /**
     * Получить группу с наименьшим приоритетом.
     *
     * @method _lowestPriorityGroup
     * @param {Array<object>} groups Список групп, в которых производится поиск группы.
     * @param {function} condition Функция с дополнительным условием проверки.
     * @private
     * @return {object} Группа с наименьшим приоритетом.
     * @static
     */
    static _lowestPriorityGroup(groups, condition) {
      let priorityCriteria = Infinity;
      let resultGroup = null;

      for (const group of groups) {
        if (group.priority < priorityCriteria && condition(group)) {
          priorityCriteria = group.priority;
          resultGroup = group;
        }
      }

      return resultGroup;
    }

    /**
     * Найти или создать группу "Еще".
     *
     * @method _findOrCreateGroupMore
     * @param {object} model Модель тулбара.
     * @private
     * @return {object} Группа "Еще".
     * @static
     */
    static _findOrCreateGroupMore(model) {
      const GROUP_MORE_NAME = 'MORE'; // name группы "Ещё"

      let groupMore = model.groupMore;
      if (!groupMore) {
        for (const group of model.groups) {
          if (group.name === GROUP_MORE_NAME) {
            groupMore = group;
            break;
          }
        }

        if (!groupMore) {
          groupMore = {
            caption: L(GROUP_MORE_NAME),
            isCollapsed: true,
            minimizedGroupIcon: null,
            name: GROUP_MORE_NAME
          };
          groups.push(groupMore);
        }

        model.groupMore = groupMore;
      }

      return groupMore;
    }


    /**
     * Проверяет наличие кнопок для перемещения в группу "Еще".
     *
     * @method _hasButtonToMove
     * @param {object} group Группа, в которой производится поиск.
     * @private
     * @return {boolean} True - кнопки для перемещения есть, иначе False.
     * @static
     */
    static _hasButtonToMove(group) {
      return group.buttons.some((button) => {
        return !group.isCollapsed && button.isVisible && !button.isMoved;
      });
    }

    /**
     * Пеереместить кнопку из группы в группу "Еще".
     *
     * @method _reduceGroup
     * @param {object} group Группа, из которой производится перемещение.
     * @param {object} groupMore Группа "Еще".
     * @private
     * @static
     */
    static _reduceGroup(group, groupMore) {
      for (const button of group.buttons) {
        if (button.isVisible && !button.isMoved) {
          button.isMoved = true;
          button.groupName = group.name;
          groupMore.buttons.push(button);
          break;
        }
      }
    }

    /**
     * Переместить одну кнопку в группу "Еще".
     *
     * @method _toolbarMoveOneButton
     * @param {object} model Модель тулбара.
     * @param {function} callback Функция обратного вызова.
     * @private
     * @static
     */
    static _toolbarMoveOneButton(model, callback) {
      const group = ReactToolBar._lowestPriorityGroup(model.groups, ReactToolBar._hasButtonToMove);
      if (group) {
        const groupMore = ReactToolBar._findOrCreateGroupMore(model);
        ReactToolBar._reduceGroup(group, groupMore);
        if (typeof callback === 'function' || callback instanceof Function) {
          callback();
        }
      }
    }

    /**
     * Свернуть группу.
     *
     * @method _compactGroup
     * @param {object} group Группа, которую нужно свернуть.
     * @private
     * @static
     */
    static _compactGroup(group) {
      for (const button of group.buttons) {
        if (!group.isCollapsed && button.isCaptionVisible && !button.isCollapsed) {
          button.isCollapsed = true;
          break;
        }
      }
    }

    /**
     * Свернуть одну кнопку тулбара.
     *
     * @method toolbarCollapseOneButton
     * @param {object} model Модель тулбара.
     * @param {function} callback Функция обратного вызова.
     * @static
     */
    static toolbarCollapseOneButton(model, callback) {
      const group = ReactToolBar._lowestPriorityGroup(model.groups, ReactToolBar._hasExpandedButtons);

      if (group) {
        ReactToolBar._compactGroup(group);
        if (typeof callback === 'function' || callback instanceof Function) {
          callback();
        }
      } else {
        ReactToolBar._toolbarMoveOneButton(model, callback);
      }
    }

    /**
     * Восстановить тулбар в исходное состояние.
     *
     * @method toolbarRestore
     * @param {object} model Модель тулбара.
     * @param {function} callback Функция обратного вызова.
     * @static
     */
    static toolbarRestore(model, callback) {
      for (const group of model.groups) {
        for (let buttonIndex = 0; buttonIndex < group.buttons.length; buttonIndex++) {
          const button = group.buttons[ buttonIndex ];
          button.isCollapsed = false;
          if (!group.isCollapsed && button.isMoved) {
            button.isMoved = false;
          } else if (group.isCollapsed && button.hasOwnProperty('isMoved')) {
            group.buttons.splice(buttonIndex, 1);
            buttonIndex--;
          }
        }
      }
      if (typeof callback === 'function' || callback instanceof Function) {
        callback();
      }
    }

    /**
     * Подписаться на события изменения кнопки.
     *
     * @private
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#_bindOnButtonChangeEvents
     * @param {object} button Кнопка, на события изменения которой подписывается панель действий.
     */
    _bindOnButtonChangeEvents(button) {
      var me = this;
      me._buttonChangeEvents.forEach(function(changeEventName) {
        button.bind(changeEventName, function() {
          me.fire(ReactToolBar.EVENT.CHANGE_STATE, me._model);
        });
      });
    }

    /**
     * Подписаться на события изменения группы.
     *
     * @private
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#_bindOnGroupChangeEvents
     * @param {object} group Группа, на события изменения которой подписывается панель действий.
     */
    _bindOnGroupChangeEvents(group) {
      var me = this;
      me._groupChangeEvents.forEach(function(changeEventName) {
        group.bind(changeEventName, function() {
          me.fire(ReactToolBar.EVENT.CHANGE_STATE, me._model);
        });
      });
    }

    /**
     * Вызвать событие изменения панели действий.
     * Используется для перерисовки панели действий.
     *
     * @private
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#_dispatchChangeEvent
     * @fires WebAccess.components.wrappers.toolBar.ReactToolBar.EVENT#CHANGE_STATE
     */
    _dispatchChangeEvent() {
      this.fire(this['class'].EVENT.CHANGE_STATE, this._model);
    }

    /**
     * Расширить коллекцию групп.
     *
     * @private
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#_extendGroupCollection
     */
    _extendGroupCollection() {
      var me = this;

      me.groups['create'] = me.createGroup.bind(me);
      me.groups['isExist'] = function(name) {
        return me.isGroupExist.apply(me, [name]);
      };
      me.groups['addElement'] = this.addGroup.bind(me);
      me.groups['deleteElement'] = this.removeGroup.bind(me);
      me.groups['clear'] = this.removeAllGroups.bind(me);
      me.groups['count'] = function () {
        return me._getGroups().length;
      };
      me.groups['toArray'] = function () {
        return me._getGroups.apply(me);
      };
    }

    /**
     * Расширить коллекцию кнопок.
     *
     * @private
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#_extendButtonCollection
     */
    _extendButtonCollection() {
      var me = this;

      var currentLength = me.buttons.length;
      me.buttons['create'] = me.createButton.bind(me);
      me.buttons['remove'] = me.removeButton.bind(me);
      me.buttons['isExist'] = function(name) {
        return me.isButtonExist.apply(me, [name]);
      };
      me.buttons['addElement'] = this.addButton.bind(me);
      me.buttons['deleteElement'] = this.removeButton.bind(me);
      me.buttons['clear'] = this.removeAllButtons.bind(me);
      me.buttons['count'] = function () {
        return me._getButtons().length;
      };
      me.buttons['toArray'] = function () {
        return me._getButtons.apply(me);
      };
      me.buttons['getButtonsByActionName'] = function (actionName) {
        return me._buttonsByActionName[actionName] || [];
      };
      me.buttons['getButtonsByEnabledMode'] = function (enabledMode) {
        return me._buttonsByEnabledMode[enabledMode] || [];
      };
      me.buttons['getButtonsEnabledOnAttachmentSelection'] = function () {
        return me._buttonsEnabledOnAttachmentSelection;
      };
    }

    /**
     * Получить список всех кнопок на панели действий.
     *
     * @private
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#_getButtons
     * @return {Array.<WebAccess.components.wrappers.toolBar.ReactToolBarButton>} Список кнопок панели действий.
     * @since 5.6.0.1307
     */
    _getButtons() {
      return global.utils.getAssociativeArrayValues(this.buttons).filter(function(button) {
        return button.className === 'ReactToolBarButton';
      });
    }

    /**
     * Получить список всех групп на панели действий.
     *
     * @private
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#_getGroups
     * @return {Array.<WebAccess.components.wrappers.toolBar.ReactToolBarGroup>} Список групп панели действий.
     * @since 5.6.0.1307
     */
    _getGroups() {
      return global.utils.getAssociativeArrayValues(this.groups).filter(function(group) {
        return group.className === 'ReactToolBarGroup';
      });
    }

    /**
     * Отписаться от событий изменения кнопки.
     *
     * @private
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#_unbindFromButtonChangeEvents
     * @param {object} button Кнопка, от событий изменения которой отписывается панель действий.
     */
    _unbindFromButtonChangeEvents(button) {
      this._buttonChangeEvents.forEach(function(changeEventName) {
        button.unbind(changeEventName);
      });
    }

    /**
     * Отписаться от событий изменения группы.
     *
     * @private
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#_unbindFromGroupChangeEvents
     * @param {object} group Группа, от событий изменения которой отписывается панель действий.
     */
    _unbindFromGroupChangeEvents(group) {
      this._groupChangeEvents.forEach(function(changeEventName) {
        group.unbind(changeEventName);
      });
    }

    /**
     * Добавить кнопку на панель действий.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#addButton
     * @param {string} buttonName Имя добавляемой кнопки.
     * @param {object} button Добавляемая кнопка.
     * @deprecated since 5.6. Use createButton instead.
     */
    addButton(buttonName, button) {
      this._warnAboutDeprecatedMethod(arguments.callee.name);
      this.buttons[buttonName] = button;

      // Подписаться на события изменения кнопки
      this._bindOnButtonChangeEvents(button);
    }

    /**
     * Добавить группу на панель действий.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#addGroup
     * @param {string} groupName Имя добавляемой группы.
     * @param {object} group Добавляемая группа.
     * @deprecated since 5.6. Use createGroup instead.
     */
    addGroup(groupName, group) {
      this._warnAboutDeprecatedMethod(arguments.callee.name);
      this.groups[groupName] = group;

      // Подписаться на события изменения группы
      this._bindOnGroupChangeEvents(group);
    }

    /**
     * Создает кнопку на панели действий.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#createButton
     * @param {string} toolGroupName Имя группы кнопок, в которую добавляется кнопка.
     * @param {string | object} buttonName Имя создаваемой кнопки или объект {name: "", text:"", action:"", icon:""}
	 * @param {BUTTON} [opt_smallSized] Отображение или скрытие заголовка кнопки. Возможные значения: LARGE, SMALL.
     * @throws ERR_SPECIFIED_BUTTONS_GROUP_DOES_NOT_EXIST – указанной группы кнопок не существует;
     * @throws ERR_BUTTON_NAME_IS_NOT_SPECIFIED_PROPERLY – неверно указано имя кнопки;
     * @throws ERR_BUTTON_WITH_THE_SAME_NAME_ALREADY_EXIST – кнопка с указанным именем уже существует.
     * @return {WebAccess.components.wrappers.toolBar.ReactToolBarButton} Объект типа WebAccess.components.wrappers.toolBar.ReactToolBarButton.
     */
    createButton(toolGroupName, buttonName, opt_smallSized) {
      // Найти нужную группу в модели
      var groupModel = this._model.groups.find(function(currentGroupModel){
        return currentGroupModel.name === toolGroupName;
      });
      if(!groupModel) {
        console.error(L('ERR_SPECIFIED_BUTTONS_GROUP_DOES_NOT_EXIST'), toolGroupName);
      }

      // Сформировать модель
      var buttonModel;
      switch (typeof buttonName) {
        case 'string':
          buttonModel = {
            name: buttonName
          };
          break;
        case 'object':
          buttonModel = buttonName;
          break;
        default:
          console.error(L('ERR_BUTTON_NAME_IS_NOT_SPECIFIED_PROPERLY'));
      }

      // Дополнить модель
      var buttonLabel = L('NEW_BUTTON');
      var newButtonModel = {
        icon: buttonModel.icon || 'Doc/doc_32.png',
        isCaptionVisible: buttonModel.isCaptionVisible || !opt_smallSized,
        jsAction: buttonModel.jsAction || buttonModel.action || 'alert(L(\'WRN_USE_CLICK_OR_BIND_METHODS_TO_SET_THE_ACTION\'));',
        name: buttonModel.name,
        caption: buttonModel.text || buttonLabel,
        tooltip: buttonModel.toolTip || buttonModel.tooltip || buttonLabel,
        isVisible: true,
        isEnabled: true
      };

      // Проверить, что еще не была создана кнопка с таким именем
      if(this.isButtonExist(newButtonModel.name)) {
        console.error(L('ERR_BUTTON_WITH_THE_SAME_NAME_ALREADY_EXIST'), name);
      }

      // Добавить новую модель кнопки в модель тулбара
      groupModel.buttons.push(newButtonModel);

      this._dispatchChangeEvent();

      // Создать кнопку
      var button = new WebAccess.components.wrappers.toolBar.ReactToolBarButton(newButtonModel, toolGroupName);
      this.buttons[newButtonModel.name] = button;

      // Подписаться на события изменения кнопки
      this._bindOnButtonChangeEvents(button);

      return button;
    }

    /**
     * Создает группу кнопок на панели действий.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#createGroup
     * @param {string | object} toolGroupName Имя группы кнопок или объект {name: "", label:"", action:"", icon:""}
     * @param {string} toolGroupNameToAppendAfter Имя существующей группы кнопок, после которой добавится новая группа.
     * @throws ERR_GROUP_NAME_IS_NOT_SPECIFIED_PROPERLY Неверно указано имя группы.
     * @throws ERR_GROUP_WITH_THE_SAME_NAME_ALREADY_EXIST Группа с указанным именем уже существует.
     * @throws ERR_SPECIFIED_BUTTONS_GROUP_DOES_NOT_EXIST Группа, после которой добавляется новая группа, не существует.
     * @returns {WebAccess.components.wrappers.toolBar.ReactToolBarGroup} Объект типа ReactToolBarGroup.
     */
    createGroup(toolGroupName, toolGroupNameToAppendAfter) {
      // Сформировать модель
      var groupModel;
      switch (typeof toolGroupName) {
        case 'string':
          groupModel = {
            name: toolGroupName
          };
          break;
        case 'object':
          groupModel = toolGroupName;
          break;
        default:
          console.error(L('ERR_GROUP_NAME_IS_NOT_SPECIFIED_PROPERLY'));
      }

      // Дополнить модель
      var newGroupModel = {
        buttons: groupModel.buttons || [],
        icon: groupModel.icon || groupModel.icons || 'Doc/unknown_32.png',
        name: groupModel.name,
        text: groupModel.text || L('NEW_GROUP')
      };

      // Проверим наличие группы с таким именем
      if(this.isGroupExist(newGroupModel.name)) {
        console.error(L('ERR_GROUP_WITH_THE_SAME_NAME_ALREADY_EXIST'), newGroupModel.name);
      }

      // Добавим группу в коллекцию
      if(typeof toolGroupNameToAppendAfter === 'string') {
        var indexToPaste;
        if(toolGroupNameToAppendAfter === '') {
          indexToPaste = 0;
        } else {
          // Если группы с указанным нет - выводим ошибку
          if(!this.isGroupExist(toolGroupNameToAppendAfter)) {
            console.error(L('ERR_SPECIFIED_BUTTONS_GROUP_DOES_NOT_EXIST'));
          }

          // Получаем индекс для вставки
          indexToPaste = this._model.groups.findIndex(function(groupModel) {
            return groupModel.name === toolGroupNameToAppendAfter;
          });
          indexToPaste++;
        }

        // Вставляем группу
        this._model.groups.splice(indexToPaste, 0, newGroupModel);

      } else {
        this._model.groups.push(newGroupModel);
      }

      this._dispatchChangeEvent();

      var group = new WebAccess.components.wrappers.toolBar.ReactToolBarGroup(newGroupModel);
      this.groups[newGroupModel.name] = group;

      // Подписаться на события изменения группы
      this._bindOnGroupChangeEvents(group);

      return group;
    }

    /**
     * Получает кнопку по имени.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#getButtonByName
     * @param {string} buttonName Имя кнопки.
     * @returns {object} Кнопка.
     */
    getButtonByName(buttonName) {
      return this.buttons[buttonName];
    }

    /**
     * Получает число кнопок на панели действий.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#getButtonsLength
     * @returns {number} Число кнопок на панели действий.
     */
    getButtonsLength() {
      return Object.keys(this.buttons).length;
    }

    /**
     * Получает группу кнопок по имени.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#getGroupByName
     * @param {string} groupName Имя группы.
     * @returns {object} Группа кнопок.
     */
    getGroupByName(groupName) {
      return this.groups[groupName];
    }

    /**
     * Получает число групп кнопок на панели действий.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#getGroupsLength
     * @returns {number} Число групп кнопок на панели действий.
     */
    getGroupsLength() {
      return Object.keys(this.groups).length;
    }

    /**
     * Получает список кнопок панели действий, которые зависят от указанного прикладного действия.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#getButtonsByActionName
     * @param {string} actionName Имя прикладного действия.
     * @returns {array} Массив кнопок панели действий, которые зависят от указанного прикладного действия.
     */
    getButtonsByActionName(actionName) {
      var buttons = this._buttonsByActionName[actionName.toLowerCase()];
      return buttons ? buttons : [];
    }
    //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~//

    /**
     * Получает список кнопок панели действий по режиму доступности.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#getButtonsByEnabledMode
     * @param {ACTION_ENABLED_MODE} enabledMode Режим доступности действия.
     * @returns {array} Массив кнопок панели действий, для которых установлен соответствующий режим доступности.
     */
    getButtonsByEnabledMode(enabledMode) {
      var buttons = this._buttonsByEnabledMode[enabledMode];
      return buttons ? buttons : [];
    }
    //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~//

    /**
     * Получает список кнопок панели действий, доступность которых изменяется в зависимости от выбора вложений.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#getButtonsEnabledOnAttachmentSelection
     * @returns {array} Массив кнопок панели действий, доступность которых изменяется в зависимости от выбора вложений.
     */
    getButtonsEnabledOnAttachmentSelection() {
      return this._buttonsEnabledOnAttachmentSelection;
    }

    /**
     * Проверяет наличие кнопки на панели действий по имени.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#isButtonExist
     * @param {string} buttonName Имя кнопки.
     * @returns {boolean} True - кнопка есть на панели действий, иначе False.
     */
    isButtonExist(buttonName) {
      return this._getButtons().some(function(button) {
        return button.name === buttonName;
      });
    }

    /**
     * Проверяет наличие группы кнопок на панели действий по имени.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#isGroupExist
     * @param {string} groupName Имя группы.
     * @returns {boolean} True - группа есть на панели действий, иначе False.
     */
    isGroupExist(groupName) {
      return this._getGroups().some(function (group) {
        return group.name === groupName;
      });
    }

    /**
     * Удаляет все кнопки на панели действий.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#removeAllButtons
     * @returns {WebAccess.components.wrappers.toolBar.ReactToolBar} Объект типа WebAccess.components.wrappers.toolBar.ReactToolBar.
     */
    removeAllButtons() {
      var me = this;
      // Удалим все кнопки из всех групп в моделях
      me._model.groups.forEach(function(groupModel) {
        groupModel.buttons = [];
      });

      // Отпишемся от событий изменения всех кнопок
      global.utils.getAssociativeArrayValues(me.buttons).forEach(function(button) {
        me._unbindFromButtonChangeEvents(button);
      });

      // Удалим все кнопки из коллекции
      me.buttons = [];

      this._dispatchChangeEvent();

      return me;
    }

    /**
     * Удаляет все группы кнопок на панели действий.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#removeAllGroups
     * @returns {WebAccess.components.wrappers.toolBar.ReactToolBar} Объект типа WebAccess.components.wrappers.toolBar.ReactToolBar.
     */
    removeAllGroups() {
      var me = this;

      // Отписаться от событий изменения всех групп и кнопок
      global.utils.getAssociativeArrayValues(me.groups).forEach(function(group) {
        me._unbindFromGroupChangeEvents(group);
      });
      global.utils.getAssociativeArrayValues(me.buttons).forEach(function(button) {
        me._unbindFromButtonChangeEvents(button);
      });

      // Удалить группы из модели
      me._model.groups = [];

      // Удалить все группы и кнопки из коллекций
      me.buttons = [];
      me.groups = [];

      this._dispatchChangeEvent();

      return me;
    }

    /**
     * Удаляет кнопку на панели действий.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#removeButton
     * @param {string} buttonName Имя кнопки.
     * @returns {WebAccess.components.wrappers.toolBar.ReactToolBar} Объект типа WebAccess.components.wrappers.toolBar.ReactToolBar.
     */
    removeButton(buttonName) {
      // Найти кнопку в модели тулбара
      var isFounded = false;
      this._model.groups.some(function(groupModel) {
        groupModel.buttons = groupModel.buttons.filter(function (buttonModel) {
          if(buttonModel.name === buttonName) {
            isFounded = true;
            return false;
          } else {
            return true;
          }
        });
        return isFounded;
      });

      if(isFounded) {
        // Отписаться от событий изменения кнопки
        this._unbindFromButtonChangeEvents(this.buttons[buttonName]);

        // Удалить кнопку из коллекции кнопок
        delete this.buttons[buttonName];

        this._dispatchChangeEvent();
      }

      return this;
    }

    /**
     * Удаляет группу кнопок на панели действий.
     *
     * @method WebAccess.components.wrappers.toolBar.ReactToolBar#removeGroup
     * @param {string} groupName Имя удаляемой группы.
     * @returns {WebAccess.components.wrappers.toolBar.ReactToolBar} Объект типа WebAccess.components.wrappers.toolBar.ReactToolBar.
     */
    removeGroup(groupName) {
      if(this.isGroupExist(groupName)) {
        // Удалить группу в модели
        var index = this._model.groups.findIndex(function(groupModel) {
          return groupModel.name === groupName;
        });
        this._model.groups = this.model.groups.splice(index, 1);

        // Отписаться от событий изменения группы
        this._unbindFromGroupChangeEvents(this.groups[groupName]);

        // Удалить группу из коллекции
        delete this.groups[groupName];

        this._dispatchChangeEvent();
      }

      return this;
    }
  }
  
  /*, {
    EVENT: {
      // Событие, срабатывающее при изменении модели панели действий.
      CHANGE_STATE: 'changeState'
    }
  });*/

  global.components.wrappers.toolBar.ReactToolBar = ReactToolBar;

})(window.WebAccess);